import { Outlet, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { BookOpen, BarChart3, ShieldCheck, Users } from 'lucide-react'

const features = [
  { icon: BarChart3, text: 'Real-time financial reports' },
  { icon: ShieldCheck, text: 'Bank reconciliation & audit logs' },
  { icon: Users, text: 'Multi-client firm management' },
]

export default function AuthLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <Navigate to="/" replace />

  return (
    <div className="min-h-screen flex">
      {/* Left panel — Xero-style brand panel */}
      <div className="hidden lg:flex lg:w-[480px] flex-col justify-between p-10 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #002A46 0%, #0078C8 100%)' }}>
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />
        {/* Glow orb */}
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, #4db9ed 0%, transparent 70%)' }}
        />

        <div className="relative">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 bg-white/15 backdrop-blur rounded-[8%] flex items-center justify-center border border-white/20">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-[21px] font-bold tracking-tight text-white">LedgerPro</span>
          </div>
          <h1 className="text-4xl font-bold leading-tight mb-4 text-white">
            Beautiful accounting<br />for your firm
          </h1>
          <p className="text-blue-200/80 text-[15px] leading-relaxed mb-10">
            Manage client ledgers, reconcile bank statements, post journals,
            and generate financial reports — all in one dashboard.
          </p>

          <div className="space-y-4">
            {features.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-[8%] bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-blue-200" />
                </div>
                <span className="text-[13px] text-blue-100/90">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="relative text-blue-300/50 text-[11px]">&copy; 2026 LedgerPro. All rights reserved.</p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center bg-[#F2F3F4] px-6">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-9 h-9 bg-[#0078C8] rounded-[8%] flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <span className="text-[17px] font-bold text-[#000A1E]">LedgerPro</span>
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  )
}
